import { useMemo, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, BadgePercent, CheckCircle2, Receipt, Search, Wallet } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { BillingActions } from "@/components/billing/BillingActions";
import { BillingAmount, BillingSituation } from "@/components/billing/BillingCells";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { recentMonths, useBillingMonth, useOverdueBillings } from "@/hooks/use-billing";
import { usePagination } from "@/hooks/use-pagination";
import { amountOf } from "@/lib/billing";
import { formatCurrency, formatDate, formatNumber, matches } from "@/lib/format";
import type { SellerBilling } from "@/types";

type PaidFilter = "todos" | "abertas" | "pagas" | "ignoradas";

function BillingTable({
  rows,
  isLoading,
  emptyTitle,
  emptyDescription,
  resetKey,
}: {
  rows: SellerBilling[];
  isLoading: boolean;
  emptyTitle: string;
  emptyDescription: string;
  /** Muda com a busca e o filtro, para a lista voltar à primeira página. */
  resetKey: string;
}) {
  // As faturas do mês são uma por estabelecimento e cabem numa leitura; os
  // totais do topo dependem de todas elas. O que pagina é o desenho da tabela.
  const pagination = usePagination(rows, resetKey);

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyState icon={Receipt} title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <>
      <Table className="table-stacked">
        <TableHeader>
          <TableRow>
            <TableHead>Estabelecimento</TableHead>
            <TableHead className="hidden lg:table-cell">Receita entregue</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead className="hidden md:table-cell">Vencimento</TableHead>
            <TableHead>Situação</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagination.pageItems.map((billing) => (
            <TableRow key={billing.id}>
              <TableCell className="cell-title">
                {billing.sellerId ? (
                  <Link
                    href={`/vendedores/${billing.sellerId}`}
                    className="font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {billing.sellerName || "Sem nome"}
                  </Link>
                ) : (
                  <span className="font-semibold">{billing.sellerName || "Sem nome"}</span>
                )}
                <p className="truncate text-caption text-muted-foreground">
                  {formatNumber(billing.deliveredOrdersCount ?? 0)} pedidos entregues
                  {billing.platformFeePercentage != null
                    ? `, taxa de ${billing.platformFeePercentage}%`
                    : ""}
                </p>
              </TableCell>
              <TableCell className="hidden lg:table-cell tabular-nums">
                {formatCurrency(billing.totalRevenue ?? 0)}
              </TableCell>
              <TableCell className="font-semibold tabular-nums">
                <BillingAmount billing={billing} />
              </TableCell>
              <TableCell className="hidden md:table-cell">{formatDate(billing.dueDate)}</TableCell>
              <TableCell>
                <BillingSituation billing={billing} />
              </TableCell>
              <TableCell className="cell-actions">
                <BillingActions billing={billing} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination {...pagination.paginationProps} itemLabel="faturas" />
    </>
  );
}

export default function Billing() {
  const months = useMemo(() => recentMonths(12), []);
  const [period, setPeriod] = useState(`${months[0].year}-${months[0].month}`);
  const [search, setSearch] = useState("");
  const [paid, setPaid] = useState<PaidFilter>("todos");

  const [year, month] = period.split("-").map(Number);
  const monthly = useBillingMonth(year, month);
  const overdue = useOverdueBillings();

  const monthRows = useMemo(() => {
    const all = monthly.data ?? [];
    return all.filter((billing) => {
      if (search && !matches(billing.sellerName, search)) return false;
      if (paid === "abertas") return !billing.paid;
      if (paid === "pagas") return billing.paid === true && !billing.waived;
      if (paid === "ignoradas") return billing.waived === true;
      return true;
    });
  }, [monthly.data, search, paid]);

  const overdueRows = useMemo(() => {
    const all = overdue.data ?? [];
    if (!search) return all;
    return all.filter((billing) => matches(billing.sellerName, search));
  }, [overdue.data, search]);

  const totals = useMemo(() => {
    const all = monthly.data ?? [];
    let billed = 0;
    let received = 0;
    let open = 0;
    let couponCredit = 0;
    let waived = 0;
    let waivedCount = 0;
    for (const billing of all) {
      const value = amountOf(billing);
      // A comissão ignorada fica fora do faturado (o `platformFee` dela já é
      // zero) e é somada à parte, para o mês mostrar quanto deixou de entrar.
      if (billing.waived) {
        waived += billing.waivedPlatformFee ?? 0;
        waivedCount += 1;
      }
      // Fatura absorvida já está contada dentro da fatura que a absorveu:
      // somá-la de novo cobraria a mesma dívida duas vezes no total do mês.
      if (billing.accumulatedIntoBillingId) continue;
      billed += value;
      couponCredit += billing.platformCouponCredit ?? 0;
      if (billing.paid) received += value;
      else open += value;
    }
    return { billed, received, open, couponCredit, waived, waivedCount, count: all.length };
  }, [monthly.data]);

  const overdueTotal = useMemo(
    () => (overdue.data ?? []).reduce((sum, b) => sum + amountOf(b), 0),
    [overdue.data],
  );

  return (
    <>
      <PageHeader
        title="Faturamento"
        description="Faturas de todos os estabelecimentos, com baixa manual, fatura ignorada, reenvio e recálculo."
        actions={
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-full sm:w-52" aria-label="Escolher o mês">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <MetricCard
          label="Faturado no mês"
          value={formatCurrency(totals.billed)}
          hint={
            totals.couponCredit > 0
              ? `${formatNumber(totals.count)} faturas, já com ${formatCurrency(totals.couponCredit)} de cupons do Zé Padoca abatidos`
              : `${formatNumber(totals.count)} faturas`
          }
          icon={Receipt}
          loading={monthly.isLoading}
        />
        <MetricCard
          label="Recebido"
          value={formatCurrency(totals.received)}
          hint="Faturas quitadas do mês"
          icon={CheckCircle2}
          tone="ok"
          loading={monthly.isLoading}
        />
        <MetricCard
          label="Em aberto"
          value={formatCurrency(totals.open)}
          hint="Ainda não quitadas no mês"
          icon={Wallet}
          tone="warning"
          loading={monthly.isLoading}
        />
        <MetricCard
          label="Comissão ignorada"
          value={formatCurrency(totals.waived)}
          hint={`${formatNumber(totals.waivedCount)} faturas sem cobrança no mês`}
          icon={BadgePercent}
          loading={monthly.isLoading}
        />
        <MetricCard
          label="Vencido"
          value={formatCurrency(overdueTotal)}
          hint={`${formatNumber(overdueRows.length)} faturas, de todos os meses`}
          icon={AlertTriangle}
          tone="error"
          loading={overdue.isLoading}
        />
      </div>

      <Tabs defaultValue="mes">
        <TabsList>
          <TabsTrigger value="mes">Do mês</TabsTrigger>
          <TabsTrigger value="vencidas">Vencidas</TabsTrigger>
        </TabsList>

        <div className="mb-4 mt-4 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pelo nome do estabelecimento"
              className="pl-9"
              aria-label="Buscar fatura"
            />
          </div>
          <Select value={paid} onValueChange={(v) => setPaid(v as PaidFilter)}>
            <SelectTrigger className="w-full sm:w-44" aria-label="Filtrar por situação">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas</SelectItem>
              <SelectItem value="abertas">Em aberto</SelectItem>
              <SelectItem value="pagas">Pagas</SelectItem>
              <SelectItem value="ignoradas">Ignoradas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="mes">
          <Card>
            <CardContent className="p-0 sm:p-0">
              <BillingTable
                rows={monthRows}
                isLoading={monthly.isLoading}
                resetKey={`${period}|${search}|${paid}`}
                emptyTitle={
                  search || paid !== "todos"
                    ? "Nenhuma fatura encontrada"
                    : "Nenhuma fatura neste mês"
                }
                emptyDescription={
                  search || paid !== "todos"
                    ? "Ajuste a busca ou o filtro para ver outros resultados."
                    : "As faturas do mês são geradas pelo cálculo diário do backend, às 03:00."
                }
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vencidas">
          <Card>
            <CardContent className="p-0 sm:p-0">
              <BillingTable
                rows={overdueRows}
                isLoading={overdue.isLoading}
                resetKey={search}
                emptyTitle={search ? "Nenhuma fatura encontrada" : "Nenhuma fatura vencida"}
                emptyDescription={
                  search
                    ? "Ajuste a busca para ver outros resultados."
                    : "Todo mundo está em dia. Esta lista cobre todos os meses, não só o escolhido acima."
                }
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
